// src/pages/expense/test/ExpenseTestList.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import api from "../../../api";
import styles from "./ExpenseTestList.module.css";
import useHighlightFromQuery from "../../../hooks/useHighlightFromQuery";

const PAY_METHODS = [
  { value: "transfer", label: "계좌이체" },
  { value: "card",     label: "카드결제" },
  { value: "cash",     label: "현금결제" },
];

const emptyItem = () => ({
  name: "", unit: "", qty: "", price: "", total: "", supply: "", vat: "", remark: "",
});
const emptySalary = () => ({
  name: "", totalSalary: "", incomeTax: "", localTax: "",
  health: "", care: "", pension: "", employment: "", total: "",
});

export default function ExpenseTestList() {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    document.title = "테스트 지출목록";
    if (!location.state?.tabTitle) {
      navigate({ pathname: ".", search: location.search }, {
        replace: true,
        state: { ...(location.state || {}), tabTitle: "테스트 지출목록" },
      });
    }
  }, []); // eslint-disable-line

  const year = new Date().getFullYear();
  const [fromDate, setFromDate] = useState(`${year}-01-01`);
  const [toDate, setToDate]     = useState(`${year}-12-31`);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch]           = useState("");

  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(true);

  // 상세 모달
  const [selectedRow, setSelectedRow] = useState(null);
  const [showDetail, setShowDetail]   = useState(false);

  // 수정 모달
  const [showEdit, setShowEdit]   = useState(false);
  const [editData, setEditData]   = useState(null);
  const [saving, setSaving]       = useState(false);
  const [saveError, setSaveError] = useState("");

  // 삭제 진행 중
  const [deletingId, setDeletingId] = useState(null);

  // ✅ 전체 삭제 진행 중
  const [bulkDeleting, setBulkDeleting] = useState(false);

  /* ───────── 목록 로드 ───────── */
  const loadList = async (searchVal) => {
    setLoading(true);
    try {
      const params = { search: searchVal || undefined, ordering: "-draft_date,-seq" };
      const res    = await api.get("/expense/test/", { params });
      const list   = Array.isArray(res.data) ? res.data : res.data.results || [];
      setRows(list);
    } catch (e) {
      console.error("❌ 목록 불러오기 실패:", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadList(search); }, [search]);

  /* ───────── 기간 필터 / 합계 ───────── */
  const filtered = useMemo(() => {
    const from = new Date(fromDate);
    const to   = new Date(toDate);
    to.setHours(23, 59, 59, 999);
    return rows.filter((r) => {
      const d = new Date(r.execution_date);
      return d >= from && d <= to;
    });
  }, [rows, fromDate, toDate]);

  const sums = useMemo(() => {
    let supply = 0, vat = 0, transfer = 0, card = 0, cash = 0;
    filtered.forEach((r) => {
      supply += Number(r.supply_amount) || 0;
      vat    += Number(r.vat)           || 0;
      const rowTotal = (Number(r.supply_amount) || 0) + (Number(r.vat) || 0);
      if (r.pay_method === "transfer") transfer += rowTotal;
      if (r.pay_method === "card")     card     += rowTotal;
      if (r.pay_method === "cash")     cash     += rowTotal;
    });
    return { supply, vat, transfer, card, cash };
  }, [filtered]);

  /* ───────── 헬퍼 ───────── */
  const getPartnerNames = (row) => {
    if (row.partners?.length) return row.partners.map((p) => p.name).filter(Boolean).join(", ");
    return row.partner_name || "-";
  };
  const getPartnerList = (row) => {
    if (row.partners?.length) return row.partners;
    if (row.partner_name)     return [{ name: row.partner_name, id: row.partner_id }];
    return [];
  };

  /* ───────── 상세 모달 ───────── */
  const openDetail  = (row) => { setSelectedRow(row); setShowDetail(true); };
  const closeDetail = () => { setShowDetail(false); setSelectedRow(null); };

  /* ───────── 수정 모달 ───────── */
  const openEdit = (row) => {
    setEditData({
      id:               row.id,
      draft_no:         row.draft_no          || "",
      draft_date:       row.draft_date         || "",
      execution_date:   row.execution_date     || "",
      pay_method:       row.pay_method         || "transfer",
      title:            row.title              || "",
      service_type:     row.service_type       || "",
      service_content:  row.service_content    || "",
      service_location: row.service_location   || "",
      other_count:      row.other_count        ?? 0,
      supply_amount:    row.supply_amount      ?? "",
      vat:              row.vat                ?? "",
      remark:           row.remark             || "",
      partners: (row.partners?.length ? row.partners : [])
                  .map((p) => ({ name: p.name || "", id: p.id || "" })),
      items: (row.items?.length ? row.items : [])
               .map((it) => ({
                 name: it.name || "", unit: it.unit || "",
                 qty: it.qty ?? "", price: it.price ?? "",
                 total: it.total ?? "", supply: it.supply ?? "",
                 vat: it.vat ?? "", remark: it.remark || "",
               })),
      salaries: (row.salaries?.length ? row.salaries : [])
                  .map((s) => ({
                    name: s.name || "", totalSalary: s.totalSalary ?? "",
                    incomeTax: s.incomeTax ?? "", localTax: s.localTax ?? "",
                    health: s.health ?? "", care: s.care ?? "",
                    pension: s.pension ?? "", employment: s.employment ?? "",
                    total: s.total ?? "",
                  })),
    });
    setSaveError("");
    setShowEdit(true);
  };
  const closeEdit = () => { setShowEdit(false); setEditData(null); };

  // ✅ 원본 데이터 열기(?highlight=id) — 대상 행을 찾아 스크롤+하이라이트, 수정 모달까지 자동 오픈
  const isHighlighted = useHighlightFromQuery(filtered, { setFromDate, setToDate, onFound: openEdit });

  /* ───────── 삭제 ───────── */
  const handleDelete = async (row) => {
    const confirmed = window.confirm(
      `삭제하시겠습니까?\n\n기안번호: ${row.draft_no}\n제목: ${row.title}\n\n삭제 후 복구할 수 없습니다.`
    );
    if (!confirmed) return;
    setDeletingId(row.id);
    try {
      await api.delete(`/expense/test/${row.id}/`);
      await loadList(search);
    } catch (e) {
      console.error(e);
      const msg = e.response?.data
        ? JSON.stringify(e.response.data).slice(0, 200)
        : e.message;
      alert(`삭제 실패: ${msg}`);
    } finally {
      setDeletingId(null);
    }
  };

  /* ───────── 테스트 데이터 전체 삭제 ───────── */
  const handleBulkDelete = async () => {
    // ✅ 화면 필터와 무관하게 서버에 존재하는 전체 건수를 대상으로 한다 (rows = 필터 적용 전 전체 목록)
    const totalCount = rows.length;
    if (totalCount === 0) {
      alert("삭제할 테스트 데이터가 없습니다.");
      return;
    }
    const confirmed = window.confirm(
      `테스트 지출 데이터 전체 ${totalCount}건을 삭제하시겠습니까?\n\n화면 필터와 무관하게 테스트 카테고리의 모든 데이터가 삭제되며, 삭제 후 복구할 수 없습니다.`
    );
    if (!confirmed) return;

    setBulkDeleting(true);
    try {
      const { data } = await api.delete("/expense/test/bulk-delete/");
      alert(`✅ ${data.deleted}건 삭제 완료`);
      await loadList(search);
    } catch (e) {
      console.error(e);
      const msg = e.response?.data
        ? JSON.stringify(e.response.data).slice(0, 200)
        : e.message;
      alert(`전체 삭제 실패: ${msg}`);
    } finally {
      setBulkDeleting(false);
    }
  };

  /* ───────── editData 변경 헬퍼 ───────── */
  const setField = (key, val) => setEditData((prev) => ({ ...prev, [key]: val }));

  const setPartnerField = (idx, val) =>
    setEditData((prev) => {
      const partners = [...prev.partners];
      partners[idx] = { ...partners[idx], name: val };
      return { ...prev, partners };
    });
  const addPartner    = () => setEditData((p) => ({ ...p, partners: [...p.partners, { name: "", id: "" }] }));
  const removePartner = (idx) =>
    setEditData((p) => ({ ...p, partners: p.partners.filter((_, i) => i !== idx) }));

  const setItemField = (idx, key, val) =>
    setEditData((prev) => {
      const items = [...prev.items];
      items[idx] = { ...items[idx], [key]: val };
      return { ...prev, items };
    });
  const addItem    = () => setEditData((p) => ({ ...p, items: [...p.items, emptyItem()] }));
  const removeItem = (idx) =>
    setEditData((p) => ({ ...p, items: p.items.filter((_, i) => i !== idx) }));

  const setSalaryField = (idx, key, val) =>
    setEditData((prev) => {
      const salaries = [...prev.salaries];
      salaries[idx] = { ...salaries[idx], [key]: val };
      return { ...prev, salaries };
    });
  const addSalary    = () => setEditData((p) => ({ ...p, salaries: [...p.salaries, emptySalary()] }));
  const removeSalary = (idx) =>
    setEditData((p) => ({ ...p, salaries: p.salaries.filter((_, i) => i !== idx) }));

  /* ───────── 저장 ───────── */
  const handleSave = async () => {
    if (!editData) return;
    setSaving(true);
    setSaveError("");
    try {
      const payload = {
        draft_date:       editData.draft_date,
        execution_date:   editData.execution_date,
        pay_method:       editData.pay_method,
        service_type:     editData.service_type,
        service_content:  editData.service_content,
        service_location: editData.service_location,
        other_count:      Number(editData.other_count) || 0,
        supply_amount:    editData.supply_amount !== "" ? Number(editData.supply_amount) : 0,
        vat:              editData.vat           !== "" ? Number(editData.vat)           : 0,
        remark:           editData.remark,
        partners: editData.partners
          .filter((p) => p.name.trim())
          .map((p) => ({ name: p.name, id: p.id || undefined })),
        items: editData.items
          .filter((it) => it.name.trim())
          .map((it) => ({
            name: it.name, unit: it.unit,
            qty:    it.qty    !== "" ? Number(it.qty)    : 0,
            price:  it.price  !== "" ? Number(it.price)  : 0,
            total:  it.total  !== "" ? Number(it.total)  : 0,
            supply: it.supply !== "" ? Number(it.supply) : 0,
            vat:    it.vat    !== "" ? Number(it.vat)    : 0,
            remark: it.remark,
          })),
        salaries: editData.salaries
          .filter((s) => s.name.trim())
          .map((s) => ({
            name:        s.name,
            totalSalary: s.totalSalary !== "" ? Number(s.totalSalary) : 0,
            incomeTax:   s.incomeTax   !== "" ? Number(s.incomeTax)   : 0,
            localTax:    s.localTax    !== "" ? Number(s.localTax)    : 0,
            health:      s.health      !== "" ? Number(s.health)      : 0,
            care:        s.care        !== "" ? Number(s.care)        : 0,
            pension:     s.pension     !== "" ? Number(s.pension)     : 0,
            employment:  s.employment  !== "" ? Number(s.employment)  : 0,
            total:       s.total       !== "" ? Number(s.total)       : 0,
          })),
      };
      await api.patch(`/expense/test/${editData.id}/`, payload);
      closeEdit();
      await loadList(search);
    } catch (e) {
      console.error(e);
      const msg = e.response?.data
        ? JSON.stringify(e.response.data).slice(0, 200)
        : e.message;
      setSaveError(`저장 실패: ${msg}`);
    } finally {
      setSaving(false);
    }
  };

  const downloadExcel = () => alert("📌 엑셀 다운로드 API 준비 필요함.");

  if (loading) return <div style={{ padding: 16 }}>불러오는 중…</div>;

  return (
    <div style={{ padding: 16 }}>
      <h2>테스트 지출목록</h2>

      {/* ── 필터바 ── */}
      <div className={styles.filterBar}>
        <div className={styles.filterItem}>
          <div className={styles.label}>기간</div>
          <div className={styles.dateRow}>
            <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className={styles.inputBase} />
            <span className={styles.dateDash}>~</span>
            <input type="date" value={toDate}   onChange={(e) => setToDate(e.target.value)}   className={styles.inputBase} />
          </div>
        </div>
        <div className={styles.filterItem}>
          <div className={styles.label}>검색</div>
          <div className={styles.searchRow}>
            <input
              placeholder="기안번호/거래처/제목/비고 검색"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && setSearch(searchInput.trim())}
              className={`${styles.inputBase} ${styles.inputSearch}`}
            />
            <button onClick={() => setSearch(searchInput.trim())} className={styles.btn}>검색</button>
            <button
              onClick={() => {
                setSearch(""); setSearchInput("");
                setFromDate(`${year}-01-01`); setToDate(`${year}-12-31`);
              }}
              className={styles.btn}
            >
              초기화
            </button>
            <button onClick={downloadExcel} className={styles.btnPrimary}>엑셀 다운로드</button>
            <button
              onClick={handleBulkDelete}
              className={styles.btnDangerOutline}
              disabled={bulkDeleting}
            >
              {bulkDeleting ? "삭제 중…" : "🗑 테스트 데이터 전체 삭제"}
            </button>
          </div>
        </div>
      </div>

      {/* ── 목록 테이블 ── */}
      <table className={styles.table}>
        <thead>
          <tr>
            <th>작성일</th>
            <th>기안번호</th>
            <th>거래처</th>
            <th>제목</th>
            <th>총금액</th>
            <th>공급가액</th>
            <th>부가세</th>
            <th>계좌이체</th>
            <th>카드</th>
            <th>현금</th>
            <th>비고</th>
            <th>상세</th>
            <th>수정</th>
            <th>삭제</th>
          </tr>
        </thead>
        <tbody>
          {/* 합계 행 */}
          <tr style={{ background: "#fafafa", fontWeight: "bold" }}>
            <td colSpan="4" style={{ textAlign: "center" }}>합계</td>
            <td style={{ textAlign: "right" }}>{(sums.supply + sums.vat).toLocaleString()}</td>
            <td style={{ textAlign: "right" }}>{sums.supply.toLocaleString()}</td>
            <td style={{ textAlign: "right" }}>{sums.vat.toLocaleString()}</td>
            <td style={{ textAlign: "right" }}>{sums.transfer.toLocaleString()}</td>
            <td style={{ textAlign: "right" }}>{sums.card.toLocaleString()}</td>
            <td style={{ textAlign: "right" }}>{sums.cash.toLocaleString()}</td>
            <td></td><td></td><td></td><td></td>
          </tr>

          {filtered.map((r) => {
            const supplyAmt = Number(r.supply_amount) || 0;
            const vatAmt    = Number(r.vat)           || 0;
            const totalAmt  = supplyAmt + vatAmt;
            return (
              <tr
                key={r.id}
                id={`row-${r.id}`}
                style={isHighlighted(r.id) ? { background: "#fff59d", transition: "background 0.6s" } : undefined}
              >
                <td>{r.execution_date}</td>
                <td>{r.draft_no}</td>
                <td>{getPartnerNames(r)}</td>
                <td>{r.title}</td>
                <td style={{ textAlign: "right" }}>{totalAmt.toLocaleString()}</td>
                <td style={{ textAlign: "right" }}>{supplyAmt.toLocaleString()}</td>
                <td style={{ textAlign: "right" }}>{vatAmt.toLocaleString()}</td>
                <td style={{ textAlign: "right" }}>{r.pay_method === "transfer" ? totalAmt.toLocaleString() : ""}</td>
                <td style={{ textAlign: "right" }}>{r.pay_method === "card"     ? totalAmt.toLocaleString() : ""}</td>
                <td style={{ textAlign: "right" }}>{r.pay_method === "cash"     ? totalAmt.toLocaleString() : ""}</td>
                <td>{r.remark}</td>
                <td>
                  <button className={styles.btnDetail} onClick={() => openDetail(r)}>보기</button>
                </td>
                <td>
                  <button className={styles.btnEdit} onClick={() => openEdit(r)}>수정</button>
                </td>
                <td>
                  <button
                    className={styles.btnDelete}
                    onClick={() => handleDelete(r)}
                    disabled={deletingId === r.id}
                  >
                    {deletingId === r.id ? "…" : "삭제"}
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {/* ══════════════════════════════
           상세 모달
      ══════════════════════════════ */}
      {showDetail && selectedRow && (
        <div className={styles.modalOverlay} onClick={closeDetail}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>지출 상세 내역 (테스트)</h3>
              <button onClick={closeDetail} className={styles.closeBtn}>✕</button>
            </div>
            <div className={styles.modalBody}>
              <div className={styles.section}>
                <h4>기본 정보</h4>
                <table className={styles.detailTable}>
                  <tbody>
                    <tr>
                      <th>기안번호</th><td>{selectedRow.draft_no}</td>
                      <th>작성일</th><td>{selectedRow.draft_date}</td>
                    </tr>
                    <tr>
                      <th>집행예정일</th><td>{selectedRow.execution_date}</td>
                      <th>거래방법</th>
                      <td>
                        {selectedRow.pay_method === "transfer" && "계좌이체"}
                        {selectedRow.pay_method === "card"     && "카드결제"}
                        {selectedRow.pay_method === "cash"     && "현금결제"}
                      </td>
                    </tr>
                    <tr><th>제목</th><td colSpan="3">{selectedRow.title}</td></tr>
                    <tr>
                      <th>사용내역</th>
                      <td colSpan="3">
                        구분: {selectedRow.service_type     || "-"} /
                        내용: {selectedRow.service_content  || "-"} /
                        분야: {selectedRow.service_location || "-"}
                        {selectedRow.other_count > 0 && ` (외 ${selectedRow.other_count}건)`}
                      </td>
                    </tr>
                    <tr><th>비고</th><td colSpan="3">{selectedRow.remark || "-"}</td></tr>
                  </tbody>
                </table>
              </div>

              {(() => {
                const pl = getPartnerList(selectedRow);
                return pl.length > 0 && (
                  <div className={styles.section}>
                    <h4>거래처 정보</h4>
                    <table className={styles.itemTable}>
                      <thead><tr><th style={{ width: 80 }}>순번</th><th>거래처명</th></tr></thead>
                      <tbody>{pl.map((p, i) => <tr key={i}><td style={{ textAlign: "center" }}>{i + 1}</td><td>{p.name}</td></tr>)}</tbody>
                    </table>
                  </div>
                );
              })()}

              {selectedRow.items?.length > 0 && (
                <div className={styles.section}>
                  <h4>품목 내역</h4>
                  <table className={styles.itemTable}>
                    <thead><tr><th>품목명</th><th>단위</th><th>수량</th><th>단가</th><th>금액</th><th>공급가액</th><th>부가세</th><th>비고</th></tr></thead>
                    <tbody>
                      {selectedRow.items.map((it, i) => (
                        <tr key={i}>
                          <td>{it.name}</td><td>{it.unit}</td>
                          <td style={{ textAlign: "right" }}>{it.qty?.toLocaleString()}</td>
                          <td style={{ textAlign: "right" }}>{it.price?.toLocaleString()}</td>
                          <td style={{ textAlign: "right" }}>{it.total?.toLocaleString()}</td>
                          <td style={{ textAlign: "right" }}>{it.supply?.toLocaleString()}</td>
                          <td style={{ textAlign: "right" }}>{it.vat?.toLocaleString()}</td>
                          <td>{it.remark}</td>
                        </tr>
                      ))}
                      <tr className={styles.sumRow}>
                        <td colSpan="4">합계</td>
                        <td style={{ textAlign: "right" }}>{selectedRow.items.reduce((a, b) => a + (b.total  || 0), 0).toLocaleString()}</td>
                        <td style={{ textAlign: "right" }}>{selectedRow.items.reduce((a, b) => a + (b.supply || 0), 0).toLocaleString()}</td>
                        <td style={{ textAlign: "right" }}>{selectedRow.items.reduce((a, b) => a + (b.vat    || 0), 0).toLocaleString()}</td>
                        <td></td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}

              {selectedRow.salaries?.length > 0 && (
                <div className={styles.section}>
                  <h4>인건비 내역</h4>
                  <table className={styles.itemTable}>
                    <thead><tr><th>이름</th><th>총 지급액</th><th>소득세</th><th>지방세</th><th>건강보험</th><th>장기요양</th><th>국민연금</th><th>고용보험</th><th>실수령액</th></tr></thead>
                    <tbody>
                      {selectedRow.salaries.map((s, i) => (
                        <tr key={i}>
                          <td>{s.name}</td>
                          <td style={{ textAlign: "right" }}>{s.totalSalary?.toLocaleString()}</td>
                          <td style={{ textAlign: "right" }}>{s.incomeTax?.toLocaleString()}</td>
                          <td style={{ textAlign: "right" }}>{s.localTax?.toLocaleString()}</td>
                          <td style={{ textAlign: "right" }}>{s.health?.toLocaleString()}</td>
                          <td style={{ textAlign: "right" }}>{s.care?.toLocaleString()}</td>
                          <td style={{ textAlign: "right" }}>{s.pension?.toLocaleString()}</td>
                          <td style={{ textAlign: "right" }}>{s.employment?.toLocaleString()}</td>
                          <td style={{ textAlign: "right" }}>{s.total?.toLocaleString()}</td>
                        </tr>
                      ))}
                      <tr className={styles.sumRow}>
                        <td>합계</td>
                        {["totalSalary","incomeTax","localTax","health","care","pension","employment","total"].map((k) => (
                          <td key={k} style={{ textAlign: "right" }}>
                            {selectedRow.salaries.reduce((a, b) => a + (b[k] || 0), 0).toLocaleString()}
                          </td>
                        ))}
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            <div className={styles.modalFooter}>
              <button onClick={closeDetail} className={styles.btn}>닫기</button>
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════
           수정 모달
      ══════════════════════════════ */}
      {showEdit && editData && (
        <div className={styles.modalOverlay} onClick={closeEdit}>
          <div className={styles.modalContent} style={{ maxWidth: 860 }} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>지출 수정 (테스트)</h3>
              <button onClick={closeEdit} className={styles.closeBtn}>✕</button>
            </div>
            <div className={styles.modalBody}>

              {/* 기본 정보 */}
              <div className={styles.section}>
                <h4>기본 정보</h4>
                <table className={styles.detailTable}>
                  <tbody>
                    <tr>
                      <th>기안번호</th>
                      <td><span style={{ fontSize: 13, color: "#555" }}>{editData.draft_no}</span></td>
                      <th>작성일</th>
                      <td><input type="date" className={styles.editInput} value={editData.draft_date} onChange={(e) => setField("draft_date", e.target.value)} /></td>
                    </tr>
                    <tr>
                      <th>집행예정일</th>
                      <td><input type="date" className={styles.editInput} value={editData.execution_date} onChange={(e) => setField("execution_date", e.target.value)} /></td>
                      <th>거래방법</th>
                      <td>
                        <select className={styles.editInput} value={editData.pay_method} onChange={(e) => setField("pay_method", e.target.value)}>
                          {PAY_METHODS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                        </select>
                      </td>
                    </tr>
                    <tr>
                      <th>구분명</th>
                      <td><input className={styles.editInput} value={editData.service_type} onChange={(e) => setField("service_type", e.target.value)} /></td>
                      <th>내용명</th>
                      <td><input className={styles.editInput} value={editData.service_content} onChange={(e) => setField("service_content", e.target.value)} /></td>
                    </tr>
                    <tr>
                      <th>분야명</th>
                      <td><input className={styles.editInput} value={editData.service_location} onChange={(e) => setField("service_location", e.target.value)} /></td>
                      <th>외 건수</th>
                      <td><input type="number" className={styles.editInput} style={{ width: 80 }} value={editData.other_count} onChange={(e) => setField("other_count", e.target.value)} /></td>
                    </tr>
                    <tr>
                      <th>공급가액</th>
                      <td><input type="number" className={styles.editInput} value={editData.supply_amount} onChange={(e) => setField("supply_amount", e.target.value)} /></td>
                      <th>부가세</th>
                      <td><input type="number" className={styles.editInput} value={editData.vat} onChange={(e) => setField("vat", e.target.value)} /></td>
                    </tr>
                    <tr>
                      <th>비고</th>
                      <td colSpan="3"><input className={styles.editInput} style={{ width: "100%" }} value={editData.remark} onChange={(e) => setField("remark", e.target.value)} /></td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* 거래처 */}
              <div className={styles.section}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                  <h4 style={{ margin: 0 }}>거래처 정보</h4>
                  <button className={styles.btnSmall} onClick={addPartner}>+ 추가</button>
                </div>
                <table className={styles.itemTable}>
                  <thead><tr><th style={{ width: 50 }}>순번</th><th>거래처명</th><th style={{ width: 60 }}>삭제</th></tr></thead>
                  <tbody>
                    {editData.partners.length === 0 && (
                      <tr><td colSpan="3" style={{ textAlign: "center", color: "#aaa" }}>거래처 없음</td></tr>
                    )}
                    {editData.partners.map((p, i) => (
                      <tr key={i}>
                        <td style={{ textAlign: "center" }}>{i + 1}</td>
                        <td><input className={styles.editInput} style={{ width: "100%" }} value={p.name} onChange={(e) => setPartnerField(i, e.target.value)} /></td>
                        <td style={{ textAlign: "center" }}><button className={styles.btnDanger} onClick={() => removePartner(i)}>✕</button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* 품목 */}
              <div className={styles.section}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                  <h4 style={{ margin: 0 }}>품목 내역</h4>
                  <button className={styles.btnSmall} onClick={addItem}>+ 추가</button>
                </div>
                <div style={{ overflowX: "auto" }}>
                  <table className={styles.itemTable}>
                    <thead>
                      <tr>
                        <th>품목명</th><th style={{ width: 60 }}>단위</th>
                        <th style={{ width: 70 }}>수량</th><th style={{ width: 80 }}>단가</th>
                        <th style={{ width: 90 }}>금액</th><th style={{ width: 90 }}>공급가액</th>
                        <th style={{ width: 80 }}>부가세</th><th>비고</th>
                        <th style={{ width: 50 }}>삭제</th>
                      </tr>
                    </thead>
                    <tbody>
                      {editData.items.length === 0 && (
                        <tr><td colSpan="9" style={{ textAlign: "center", color: "#aaa" }}>품목 없음</td></tr>
                      )}
                      {editData.items.map((it, i) => (
                        <tr key={i}>
                          {["name","unit","qty","price","total","supply","vat","remark"].map((k) => (
                            <td key={k}>
                              <input
                                type={["qty","price","total","supply","vat"].includes(k) ? "number" : "text"}
                                className={styles.editInput} style={{ width: "100%" }}
                                value={it[k]} onChange={(e) => setItemField(i, k, e.target.value)}
                              />
                            </td>
                          ))}
                          <td style={{ textAlign: "center" }}><button className={styles.btnDanger} onClick={() => removeItem(i)}>✕</button></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* 인건비 */}
              <div className={styles.section}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                  <h4 style={{ margin: 0 }}>인건비 내역</h4>
                  <button className={styles.btnSmall} onClick={addSalary}>+ 추가</button>
                </div>
                <div style={{ overflowX: "auto" }}>
                  <table className={styles.itemTable}>
                    <thead>
                      <tr>
                        <th>이름</th><th style={{ width: 90 }}>총지급액</th>
                        <th style={{ width: 70 }}>소득세</th><th style={{ width: 60 }}>지방세</th>
                        <th style={{ width: 80 }}>건강보험</th><th style={{ width: 70 }}>장기요양</th>
                        <th style={{ width: 80 }}>국민연금</th><th style={{ width: 80 }}>고용보험</th>
                        <th style={{ width: 90 }}>실수령액</th><th style={{ width: 50 }}>삭제</th>
                      </tr>
                    </thead>
                    <tbody>
                      {editData.salaries.length === 0 && (
                        <tr><td colSpan="10" style={{ textAlign: "center", color: "#aaa" }}>인건비 없음</td></tr>
                      )}
                      {editData.salaries.map((s, i) => (
                        <tr key={i}>
                          {["name","totalSalary","incomeTax","localTax","health","care","pension","employment","total"].map((k) => (
                            <td key={k}>
                              <input
                                type={k === "name" ? "text" : "number"}
                                className={styles.editInput} style={{ width: "100%" }}
                                value={s[k]} onChange={(e) => setSalaryField(i, k, e.target.value)}
                              />
                            </td>
                          ))}
                          <td style={{ textAlign: "center" }}><button className={styles.btnDanger} onClick={() => removeSalary(i)}>✕</button></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {saveError && (
                <div style={{ color: "red", marginTop: 8, fontSize: 13 }}>{saveError}</div>
              )}
            </div>
            <div className={styles.modalFooter}>
              <button onClick={closeEdit} className={styles.btn} disabled={saving}>취소</button>
              <button onClick={handleSave} className={styles.btnPrimary} disabled={saving}>
                {saving ? "저장 중…" : "저장"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
